import { WithColor } from '@styles/colors';
import { NCTokenType } from '@components/common/NCFile/types';
import { ChangeSummaryCategory } from '@components/ncFileVersions/components/NCFileDiffView/constants/classifyChanges.constants';

export enum ChangeType {
  ADDED = 'added',
  REMOVED = 'removed',
  UNCHANGED = 'unchanged',
}

export enum InlineChangeType {
  ADDED = 'added',
  REMOVED = 'removed',
  UNCHANGED = 'unchanged',
}

export interface InlineDiffSegment {
  value: string;
  type: InlineChangeType;
  semanticType?: NCTokenType;
  isSemanticHighlight?: boolean;
}

export interface ProcessedLine {
  oldLineNumber: number | null;
  newLineNumber: number | null;
  displayLineNumber?: number;
  content: string;
  changeType: ChangeType;
  isCollapsed?: boolean;
  key: string;
  inlineSegments?: InlineDiffSegment[];
}

export interface CollapsedSection {
  startLineOld: number;
  startLineNew: number;
  count: number;
  key: string;
}

export interface DiffRowProps extends WithColor {
  oldLine: ProcessedLine | null;
  newLine: ProcessedLine | null;
  isGroupStart: boolean;
  isGroupEnd: boolean;
  isFirstLineInDocument: boolean;
  groupPositionOld?: GroupPosition;
  groupPositionNew?: GroupPosition;
  onExpandSection?: (section: CollapsedSection) => void;
  nativeID?: string;
  // Increments on each click-to-jump that targets this row. Used as a useEffect
  // dep so even same-row re-clicks restart the highlight animation. 0 = never.
  highlightTick?: number;
}

export type DiffListItem =
  | {
      type: 'row';
      key: string;
      oldLine: ProcessedLine | null;
      newLine: ProcessedLine | null;
      isGroupStart: boolean;
      isGroupEnd: boolean;
      index: number;
      groupPositionOld?: GroupPosition;
      groupPositionNew?: GroupPosition;
    }
  | { type: 'collapsed'; key: string; section: CollapsedSection };

export interface ProcessedDiff {
  oldLines: (ProcessedLine | CollapsedSection)[];
  newLines: (ProcessedLine | CollapsedSection)[];
}

export interface VersionMetadata {
  versionId: number;
  version: string;
  badgeLabel?: string;
  pcsCount?: number | null;
  isFavorite?: boolean;
  firstEvent?: string | null;
  lastEvent?: string | null;
  firstMachine?: string | null;
  lastMachine?: string | null;
  batch?: string | null;
  memo?: string | null;
  isActive?: boolean;
}

export interface VersionPickerAnchorPosition {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
}

export type ColumnSide = 'old' | 'new';

export type NCDialect = 'klartext' | 'iso';

export type GroupPosition = 'single' | 'start' | 'middle' | 'end' | undefined;

export type LineHeightEntry = {
  values: Partial<Record<ColumnSide, number>>;
  max: number;
  subscribers: Set<(height: number) => void>;
};

export interface NCFileVersionData {
  ncFileVersionId: number;
  sequenceNumber: number;
  fileContentHash: string;
  ncFileTransferFromMachineState: string;
  isNcFileContentAvailable: boolean;
  isFavorite: boolean;
  isActive: boolean;
  firstRanOn: string;
  lastRanOn: string;
  firstRanByMachineName: string;
  lastRanByMachineName: string;
  createdOn: string;
  memo?: string | null;
}

export interface GetNCFileResult {
  data: string;
  size: number;
}

// Fetches NC file content by hash; shared by NCFileDiffView, NCFileDiffSheet, and the content-fetching hook.
export type GetNCFileContent = (hash: string, fullFile: boolean) => Promise<GetNCFileResult>;

export interface NCFileDiffViewProps {
  oldVersionData: NCFileVersionData;
  newVersionData: NCFileVersionData;
  getNCFile: GetNCFileContent;
  availableVersions?: NCFileVersionData[];
  onReplaceVersion?: (currentVersionId: number, nextVersion: NCFileVersionData) => void;
  onRequestPicker?: (target: 'old' | 'new') => void;
  onClose?: () => void;
  onToggleFavoriteVersion?: (version: NCFileVersionData) => void;
  // Filename of the NC file (e.g. "PART42.H" or "PART42.NC"). Used to detect
  // the syntax dialect (Klartext vs ISO) so the change classifier picks the
  // right token regex set.
  ncFileName?: string | null;
}

export interface VersionSelectionModalProps extends WithColor {
  visible: boolean;
  versions: NCFileVersionData[];
  currentVersion: VersionMetadata;
  otherVersionId?: number;
  onClose: () => void;
  onSelect: (version: NCFileVersionData) => void;
  anchorPosition?: VersionPickerAnchorPosition;
  column: 'left' | 'right';
}

export interface VersionHeaderProps extends WithColor {
  version: VersionMetadata;
  onPress?: (anchor: VersionPickerAnchorPosition) => void;
  isExpanded?: boolean;
  disabled?: boolean;
  onToggleFavorite?: () => void;
}

export interface DiffColumnProps extends WithColor {
  lines: (ProcessedLine | CollapsedSection)[];
  version?: VersionMetadata;
  isOldVersion: boolean;
  onExpandSection: (section: CollapsedSection) => void;
  onVersionPress?: (anchor: VersionPickerAnchorPosition) => void;
  isHeaderInteractive?: boolean;
  isHeaderExpanded?: boolean;
  onToggleFavorite?: () => void;
}

export interface ChangeSummaryEntry {
  id: string;
  category: ChangeSummaryCategory;
  label: string;
  before?: string;
  after?: string;
  rowIndex: number;
  rowKey: string;
}

export interface ChangeSummary {
  entries: ChangeSummaryEntry[];
  countsByCategory: Record<ChangeSummaryCategory, number>;
  totalChanges: number;
}
